// src/main.tsx - OPTIMIZED FOR FASTER LOADING
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './utils/routes';
import LoadingSpinner from './components/shared/LoadingSpinner';
import './index.css';

// Configure QueryClient with optimized settings for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - garbage collection
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch on component mount if data exists
      refetchOnReconnect: false, // Don't refetch on reconnect
      placeholderData: (previousData: any) => previousData, // Keep previous data while loading
    },
  },
});

// Add preload links for critical resources to speed up API calls
const addResourceHints = () => {
  const apiUrl = import.meta.env.VITE_API_URL || 'https://api.carryngo.ng';
  
  const links = [
    { rel: 'preconnect', href: apiUrl, crossOrigin: 'anonymous' },
    { rel: 'dns-prefetch', href: apiUrl },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
  ];
  
  links.forEach(link => {
    const existingLink = document.querySelector(`link[rel="${link.rel}"][href="${link.href}"]`);
    if (!existingLink) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = link.rel;
      preloadLink.href = link.href;
      if (link.crossOrigin) {
        preloadLink.crossOrigin = link.crossOrigin;
      }
      document.head.appendChild(preloadLink);
    }
  });
};

// Preload critical dashboard components after initial load
const preloadCriticalComponents = () => {
  // Use requestIdleCallback for non-critical preloading
  const schedulePreload = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 2000);
    }
  };

  schedulePreload(() => {
    // Preload based on user role if authenticated
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.role === 'customer') {
          import('./pages/Dashboard/CustomerDashboard');
          import('./pages/Dashboard/CustomerOrders');
          import('./pages/Dashboard/CustomerProfile');
        } else if (user.role === 'rider') {
          import('./pages/Dashboard/RiderDashboard');
          import('./pages/Dashboard/RiderOrders');
          import('./pages/Dashboard/RiderProfile');
        }
      } catch (e) {
        // Fallback - preload both
        import('./pages/Dashboard/CustomerDashboard');
        import('./pages/Dashboard/RiderDashboard');
      }
    } else {
      // Preload marketplace for non-authenticated users
      import('./pages/Marketplace/Marketplace');
      import('./pages/Auth/Login');
    }
  });
};

// Add fadeIn animation to document if not exists
const addFadeInAnimation = () => {
  if (!document.querySelector('#fadein-style')) {
    const style = document.createElement('style');
    style.id = 'fadein-style';
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .fade-in {
        animation: fadeIn 0.3s ease-out forwards;
      }
    `;
    document.head.appendChild(style);
  }
};

// Initialize performance optimizations
addResourceHints();
addFadeInAnimation();
preloadCriticalComponents();

// Detect if running on mobile for performance adjustments
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Create a wrapper component to handle initial loading
const AppWrapper = () => {
  const [isInitialized, setIsInitialized] = React.useState(false);
  
  React.useEffect(() => {
    // Mark as initialized after a short delay to prevent flash of loading
    const timer = setTimeout(() => setIsInitialized(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <Suspense fallback={<LoadingSpinner fullScreen text="Loading CarryNGo..." delay={300} />}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

// Render the app with optimized providers
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppWrapper />
      {process.env.NODE_ENV === 'development' && !isMobile && (
        <React.Suspense fallback={null}>
          {React.createElement(
            lazy(() => import('@tanstack/react-query-devtools').then(mod => ({ default: mod.ReactQueryDevtools }))),
            { initialIsOpen: false }
          )}
        </React.Suspense>
      )}
    </QueryClientProvider>
  </React.StrictMode>
);